import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";

import Home from "@/pages/Home";
import BusinessesPage from "@/pages/BusinessesPage";
import ServicesPage from "@/pages/ServicesPage";
import BusinessPage from "@/pages/BusinessPage";
import SearchPage from "@/pages/SearchPage";
import { NewsDetail, EventDetail, TouristDetail, JobDetail, ServiceDetail, ProductDetail, PromotionDetail } from "@/pages/DetailPages";
import ReelsPage from "@/pages/ReelsPage";

import AuthCallback from "@/pages/admin/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ResourceManager from "@/pages/admin/ResourceManager";
import LayoutCustomizer from "@/pages/admin/LayoutCustomizer";
import SectionsManager from "@/pages/admin/SectionsManager";
import Settings from "@/pages/admin/Settings";
import RewardsManager from "@/pages/admin/RewardsManager";

const AdminGuard = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 text-center">
        <div className="bg-card rounded-3xl p-8 max-w-sm">
          <h1 className="text-xl font-bold font-heading text-foreground">Acesso restrito</h1>
          <p className="text-sm text-muted-foreground mt-2">Sua conta ({user.email}) não tem permissão para acessar o painel administrativo.</p>
        </div>
      </div>
    );
  }
  return <AdminLayout />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/empresas" element={<BusinessesPage />} />
      <Route path="/servicos" element={<ServicesPage />} />
      <Route path="/empresa/:id" element={<BusinessPage />} />
      <Route path="/noticia/:id" element={<NewsDetail />} />
      <Route path="/evento/:id" element={<EventDetail />} />
      <Route path="/ponto-turistico/:id" element={<TouristDetail />} />
      <Route path="/vaga/:id" element={<JobDetail />} />
      <Route path="/servico/:id" element={<ServiceDetail />} />
      <Route path="/produto/:id" element={<ProductDetail />} />
      <Route path="/promocao/:id" element={<PromotionDetail />} />
      <Route path="/reels" element={<ReelsPage />} />
      <Route path="/buscar" element={<SearchPage />} />
      <Route path="/admin" element={<AdminGuard />}>
        <Route index element={<Dashboard />} />
        <Route path="r/:resource" element={<ResourceManager />} />
        <Route path="layout" element={<LayoutCustomizer />} />
        <Route path="sections" element={<SectionsManager />} />
        <Route path="settings" element={<Settings />} />
        <Route path="rewards" element={<RewardsManager />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <SiteProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </SiteProvider>
    </AuthProvider>
  );
}

export default App;
